/**
 * @wharfkit/wallet-plugin-mock v0.4.0
 * https://github.com/wharfkit/wallet-plugin-mock
 *
 * @license
 * Copyright (c) 2023 Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 * 1.  Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 * 2.  Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 * 3.  Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { AbstractWalletPlugin, WalletPluginMetadata, Checksum256, PermissionLevel, Signature } from '@wharfkit/session';

class WalletPluginMock extends AbstractWalletPlugin {
    /**
     * A constructor for the mock wallet that's capable of overriding most of the default values.
     *
     * @param options WalletPluginMockOptions
     */
    constructor(options) {
        super();
        /**
         * A unique string identifier for this wallet plugin.
         *
         * It's recommended this is all lower case, no spaces, and only URL-friendly special characters (dashes, underscores, etc)
         */
        this.id = 'wallet-plugin-mock';
        /**
         * The logic configuration for the wallet plugin.
         */
        this.config = {
            // Should the user interface display a chain selector?
            requiresChainSelect: false,
            // Should the user interface display a permission selector?
            requiresPermissionSelect: false,
            // Optionally specify if this plugin only works with specific blockchains.
            // supportedChains: ['73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d']
        };
        /**
         * The metadata for the wallet plugin to be displayed in the user interface.
         */
        this.metadata = WalletPluginMetadata.from({
            name: 'Mock Wallet',
            description: '',
            logo: 'base_64_encoded_image',
            homepage: 'https://github.com/wharfkit/wallet-plugin-mock',
            download: 'https://github.com/wharfkit/wallet-plugin-mock',
        });
        /**
         * A mock WalletPluginLoginResponse to be returned by the login method.
         */
        this.loginResponse = {
            chain: Checksum256.from('73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d'),
            permissionLevel: PermissionLevel.from('wharfkittest@test'),
        };
        /**
         * A mock WalletPluginSignResponse to be returned by the sign method.
         */
        this.signResponse = {
            signatures: [
                Signature.from('SIG_K1_KfqBXGdSRnVgZbAXyL9hEYbAvrZjcaxUCenD7Z3aX6yzf6MEyc4Cy3ywToD4j3SKkzSg7L1uvRUirEPHwAwrbg5c9z27Z3'),
            ],
        };
        if (options) {
            if (options.config) {
                this.config = options.config;
            }
            if (options.metadata) {
                this.metadata = options.metadata;
            }
            if (options.data) {
                this._data = options.data;
            }
            if (options.loginResponse) {
                this.loginResponse = options.loginResponse;
            }
            if (options.signResponse) {
                this.signResponse = options.signResponse;
            }
        }
    }
    /**
     * Performs the wallet logic required to login and return the chain and permission level to use.
     *
     * @param options WalletPluginLoginOptions
     * @returns Promise<WalletPluginLoginResponse>
     */
    async login(context) {
        const response = { ...this.loginResponse };
        if (context.chain) {
            response.chain = context.chain.id;
        }
        if (context.permissionLevel) {
            response.permissionLevel = context.permissionLevel;
        }
        return response;
    }
    /**
     * Performs the wallet logic required to sign a transaction and return the signature.
     *
     * @param chain ChainDefinition
     * @param resolved ResolvedSigningRequest
     * @returns Promise<Signature>
     */
    async sign() {
        return this.signResponse;
    }
}

export { WalletPluginMock };
//# sourceMappingURL=wallet-plugin-mock.m.js.map
